/** The right-hand panel when nothing has been built yet.
 *
 * It exists because an empty payoff chart teaches nothing. The half of the
 * screen that will hold the basket should, until there is one, be the
 * fastest way to get one — which for an index chain means the dozen or so
 * structures anybody actually puts on, rather than clicking four strikes by
 * hand and hoping the legs came out the right way round.
 *
 * ⭐ A CARD NAMES ITS LEGS. The exotic names are not standardised across
 * platforms, so the strikes shown on the card are the authority and the
 * label is just a handle. This is also what makes an unbuildable card
 * honest: it says which strike is missing instead of greying out mutely.
 */

import { useState, type ReactElement } from "react";
import { cn } from "@/lib/cn";
import { PayoffThumb } from "./PayoffThumb";
import type { SimChainRow, SimLeg } from "@/lib/simulatorApi";
import {
  BIASES,
  DEFAULT_WIDTH,
  TEMPLATES,
  WIDTHS,
  buildTemplate,
  type Bias,
  type StrategyTemplate,
} from "@/lib/strategyTemplates";

interface Props {
  rows: SimChainRow[];
  atm: number | null;
  at: string;
  onBuild: (legs: SimLeg[]) => void;
  expiry: string | null;
  expiries: string[];
  onExpiry: (v: string) => void;
}

/** "SELL 24200 CE · BUY 24300 CE" — the structure in the only terms that
 * cannot be misread. */
function legLine(legs: SimLeg[]): string {
  return legs
    .map((l) => {
      const lots = l.lots === 1 ? "" : ` ×${String(l.lots)}`;
      return `${l.direction} ${String(l.strike)} ${l.side}${lots}`;
    })
    .join(" · ");
}

function Card({
  template,
  rows,
  atm,
  at,
  width,
  onBuild,
}: Readonly<Props & { template: StrategyTemplate; width: number }>): ReactElement {
  const result = buildTemplate(template, rows, atm, width, at);
  return (
    <button
      type="button"
      disabled={!result.ok}
      title={template.blurb}
      onClick={() => {
        if (result.ok) onBuild(result.legs);
      }}
      className={cn(
        "flex flex-col gap-1 rounded-ctl border border-line p-2 text-left transition-colors",
        result.ok ? "hover:border-accent hover:bg-surface-2" : "cursor-not-allowed opacity-55",
      )}
    >
      {/* Drawn from the TEMPLATE, not the built legs: the outline is what
          identifies the structure, and it must be there to be recognised
          even on a session too thin to build it. */}
      <PayoffThumb legs={template.legs(width)} id={template.key} />
      <span className="text-xs font-semibold text-ink">{template.name}</span>
      <span
        className={cn(
          "font-mono text-[10px] leading-tight",
          result.ok ? "text-ink-secondary" : "text-danger",
        )}
      >
        {result.ok ? legLine(result.legs) : result.reason}
      </span>
    </button>
  );
}

function WidthPicker({
  width,
  onWidth,
}: Readonly<{ width: number; onWidth: (w: number) => void }>): ReactElement {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[11px] text-ink-secondary">Width</span>
      {WIDTHS.map((w) => (
        <button
          key={w}
          type="button"
          className={cn(
            "rounded-ctl px-2 py-0.5 text-[11px] font-medium transition-colors",
            w === width
              ? "bg-accent-bg text-accent"
              : "text-ink-secondary hover:bg-surface-2 hover:text-ink",
          )}
          onClick={() => {
            onWidth(w);
          }}
        >
          {w}
        </button>
      ))}
      <span className="text-[11px] text-ink-muted">strike{width === 1 ? "" : "s"} out</span>
    </div>
  );
}

/** Which expiry the built legs are priced against. Duplicates the chain's
 * expiry tabs on purpose: a basket built here uses ONE expiry, and having
 * to look at the other column to learn which is exactly the ambiguity that
 * gets a spread read against the wrong week. */
function ExpiryPicker({
  expiry,
  expiries,
  onExpiry,
}: Readonly<Pick<Props, "expiry" | "expiries" | "onExpiry">>): ReactElement | null {
  if (expiries.length === 0) return null;
  return (
    <select
      aria-label="Strategy expiry"
      className="input h-7 w-36 py-0 text-xs"
      value={expiry ?? ""}
      onChange={(e) => {
        onExpiry(e.target.value);
      }}
    >
      {expiries.map((e) => (
        <option key={e} value={e}>
          {e}
        </option>
      ))}
    </select>
  );
}

function BiasTabs({
  bias,
  onBias,
}: Readonly<{ bias: Bias; onBias: (b: Bias) => void }>): ReactElement {
  return (
    <div className="flex gap-1">
      {BIASES.map((b) => (
        <button
          key={b}
          type="button"
          className={cn(
            "rounded-ctl px-3 py-1 text-xs font-medium transition-colors",
            b === bias
              ? "bg-accent-bg text-accent"
              : "text-ink-secondary hover:bg-surface-2 hover:text-ink",
          )}
          onClick={() => {
            onBias(b);
          }}
        >
          {b}
        </button>
      ))}
    </div>
  );
}

/** Expiry, shelf and width — everything that decides WHICH cards show and
 * what they build. */
function Toolbar({
  expiry,
  expiries,
  onExpiry,
  bias,
  onBias,
  width,
  onWidth,
}: Readonly<
  Props & {
    bias: Bias;
    onBias: (b: Bias) => void;
    width: number;
    onWidth: (w: number) => void;
  }
>): ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <ExpiryPicker expiry={expiry} expiries={expiries} onExpiry={onExpiry} />
      <BiasTabs bias={bias} onBias={onBias} />
      <div className="ml-auto">
        <WidthPicker width={width} onWidth={onWidth} />
      </div>
    </div>
  );
}

export function StrategyLibrary(props: Readonly<Props>): ReactElement {
  const [bias, setBias] = useState<Bias>("Neutral");
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const shown = TEMPLATES.filter((t) => t.bias === bias);

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h2 className="text-sm font-semibold text-ink">Pre-built strategies</h2>
        <p className="mt-0.5 text-[11px] text-ink-secondary">
          Priced off this minute&apos;s chain. Click one to load its legs, then step the clock.
        </p>
      </div>

      <Toolbar {...props} bias={bias} onBias={setBias} width={width} onWidth={setWidth} />

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {shown.map((t) => (
          <Card key={t.key} template={t} width={width} {...props} />
        ))}
      </div>
    </div>
  );
}
